import * as React from "react";
import { CheckboxEditor } from "../../../../../../react-core/Html/CheckboxEditor";
import { MoneyTextBoxEditor } from "../../../../../../react-core/Html/Money/MoneyTextBoxEditor";
import { RadiosEditor } from "../../../../../../react-core/Html/RadiosEditor";
import { SelectEditor } from "../../../../../../react-core/Html/SelectEditor";
import { CurrencyCode } from "../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { Frequency } from "../../../../../../Wrap/Constants/Enums/Domain/Frequency";
import { SubAccountType } from "../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { MoneyOrPercentageType } from "../../../../../../Wrap/Constants/Enums/MoneyOrPercentageType";
import { SubAccount } from "../../../../../../Wrap/Models/Domains/Account/SlAccounts/SubAccount";
import { GiaContributionModel } from "../../../../../../Wrap/Models/Models/Processes/Apply/PersonalPortfolio/GiaContributionModel";
import { PensionContributionModel } from "../../../../../../Wrap/Models/Models/Processes/Apply/Pension/PensionContributionModel";
import { IsaContributionModel } from "../../../../../../Wrap/Models/Models/Processes/OneOffPayment/Isa/IsaContributionModel";
import { WhatIfToolDetailModel } from "../../../../../../Wrap/Models/Models/Processes/WhatIfTool/WhatIfToolDetailModel";
import { MoneyOrPercentage } from "../../../../../../Wrap/Models/Models/Shared/MoneyOrPercentage";
import { Alert } from "../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../Shared/Entities/Constants/AlertConstants";
import { PercentageOrMoney } from "../../../../../Shared/PercentageOrMoney/PercentageOrMoney";
import { IProcessComponentProps, ProcessComponent } from "../../../../../Shared/Process/Process/ProcessComponent";
import { getQueryParameters } from "../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { ClientAccountStore } from "../../../../Stroes/Variable";
import { FrequencyArraysMap, FrequencyTypeArrays } from "../../Common/Resource/Contents";
import { ChargesOnIllustration } from "../../Common/AdviserCharges/Components/ChargesOnIlustration";

interface IAdHocAdviserChargesProps extends IProcessComponentProps {
    data: GiaContributionModel | IsaContributionModel | PensionContributionModel;
    type: SubAccountType;
    extraInfo: WhatIfToolDetailModel;
}

interface IAdHocAdviserChargesState {
    includeOAC: boolean;
}

interface IdisplayOac {
    subAccount: SubAccount;
    oac: MoneyOrPercentage;
}

export class AdHocAdviserCharges extends ProcessComponent<IAdHocAdviserChargesProps, IAdHocAdviserChargesState> {
    constructor(props: any) {
        super(props);
        this.state = {
            includeOAC: false,
        };
    }

    componentDidMount() {
        super.componentDidMount();
        if (this.props.data?.Contribution?.OngoingAdviserChargeInstructions.length > 0) {
            this.setState({ includeOAC: true });
        }
    }

    private getProps = (key) => {
        const { onChange, data, showErrorMsgPopUp } = this.props;
        return {
            data: data[key],
            onRef: (ref) => this.appendChild(ref),
            showErrorMsgPopUp: (msg, isShow) => showErrorMsgPopUp(msg, isShow),
            onChange: (changes) => this.onDataChange(onChange, changes, data, key),
        };
    };

    private generateSubaccountOAC(): IdisplayOac[] {
        const instructions = this.props.data?.Contribution?.OngoingAdviserChargeInstructions || [];
        const detail = this.props.extraInfo?.OngoingAdviceCharge || [];
        const subAccount = (ClientAccountStore.getClientAccountInfo()?.SubAccounts || []).filter(
            (ele) =>
                ele.HierarchyId !== getQueryParameters()["subaccounthierarchyid"] &&
                ele.SubAccountType !== SubAccountType.WrapCash
        );
        const OACs: IdisplayOac[] = subAccount.map((ele) => {
            const instructionIndex = instructions.findIndex((item) => item.HierarchyId == ele.HierarchyId);
            const detailIndex = detail.findIndex((item) => item.SubAccountHierarchyId == ele.HierarchyId);
            if (instructionIndex > -1) {
                return { subAccount: ele, oac: instructions[instructionIndex]?.MoneyOrPercentage };
            } else if (detailIndex > -1) {
                return { subAccount: ele, oac: detail[detailIndex].AdviserCharge };
            } else {
                return {
                    subAccount: ele,
                    oac: {
                        Money: { Amount: 0, Currency: CurrencyCode.GBP },
                        Frequency: Frequency.Monthly,
                        Type: MoneyOrPercentageType.Money,
                        Percentage: 0,
                    },
                };
            }
        });
        return OACs;
    }

    private renderOACCheckbox(displayOac: IdisplayOac): JSX.Element {
        const { data, onChange } = this.props;
        const  isSetDefaultValue = displayOac.oac ? false : true
        return (
            <>
                <CheckboxEditor
                    label={`${displayOac.subAccount.ProductDisplayName} ${displayOac.subAccount.ClAccountId}`}
                    onChange={(v) => {
                        let instructions = [];
                        if (v) {
                            instructions = (
                                this.props.data?.Contribution?.OngoingAdviserChargeInstructions || []
                            ).concat({
                                HierarchyId: displayOac.subAccount.HierarchyId,
                                MoneyOrPercentage: displayOac.oac,
                            });
                        } else {
                            instructions = (
                                this.props.data?.Contribution?.OngoingAdviserChargeInstructions || []
                            ).filter((ele) => ele.HierarchyId == displayOac.subAccount.HierarchyId);
                        }
                        this.onDataChange(
                            onChange,
                            {
                                ...data?.Contribution,
                                OngoingAdviserChargeInstructions: instructions,
                            },
                            data?.Contribution,
                            "Contribution"
                        );
                    }}
                    value={data?.Contribution?.OngoingAdviserChargeInstructions.some(
                        (ele) => ele.HierarchyId == displayOac.subAccount.HierarchyId
                    )}
                />
                <div className="row">
                    <div className="col-12 col-md-6 col-lg-4">
                        <PercentageOrMoney
                            label={"Annual rate"}
                            data={displayOac.oac}
                            onChange={(changes) => {
                                const instructions = (
                                    this.props.data?.Contribution?.OngoingAdviserChargeInstructions || []
                                ).concat();
                                const index = instructions.findIndex(
                                    (ele) => ele.HierarchyId == displayOac.subAccount.HierarchyId
                                );
                                const newInstruction = {
                                    ...instructions[index],
                                    MoneyOrPercentage: { ...instructions[index]?.MoneyOrPercentage, ...changes },
                                };
                                instructions[index] = newInstruction;
                                this.onDataChange(
                                    onChange,
                                    {
                                        ...data?.Contribution,
                                        OngoingAdviserChargeInstructions: instructions,
                                    },
                                    data?.Contribution,
                                    "Contribution"
                                );
                            }}
                            disabled={
                                !data?.Contribution?.OngoingAdviserChargeInstructions.some(
                                    (ele) => ele.HierarchyId == displayOac.subAccount.HierarchyId
                                )
                            }
                            onRef={(ref) => this.appendChild(ref)}
                            cssPercentageOrMoneyClasses="control-size-115"
                            setDefaultValue={isSetDefaultValue}
                        />
                    </div>
                    <div className="col-12 col-md-6 col-lg-3">
                        <SelectEditor
                            options={FrequencyTypeArrays}
                            getText={(v) => {
                                if (v == Frequency.Yearly) {return "Annually";}
                                return FrequencyArraysMap[v];
                            }}
                            selected={displayOac?.oac?.Frequency}
                            label="Deduction frequency"
                            onChange={(v) => {}}
                            disabled
                        />
                    </div>
                </div>
            </>
        );
    }

    public render(): JSX.Element {
        const { data, onChange, extraInfo } = this.props;
        const oacs = this.generateSubaccountOAC();
        const adHocAdviserChargeAmount = (data?.Contribution?.AdHocAdviserCharge?.Amount) ? (data?.Contribution?.AdHocAdviserCharge?.Amount) : 0
        return (
            <>
                <h2 className="mb-22">Add Ad-hoc adviser charge</h2>
                <MoneyTextBoxEditor
                    currency={CurrencyCode.GBP}
                    value={adHocAdviserChargeAmount}
                    onChange={(v) => {
                        this.onDataChange(
                            onChange,
                            {
                                ...data?.Contribution,
                                AdHocAdviserCharge: { Amount: v, Currency: CurrencyCode.GBP },
                            },
                            data?.Contribution,
                            "Contribution"
                        );
                    }}
                    cssWrapper={"control-size-430"}
                />
                <br />
                <RadiosEditor
                    label="Would you like to include other product Ongoing Adviser Charging in the illustration?"
                    options={[true, false]}
                    getText={(o) => (o ? "Yes" : "No")}
                    onChange={(v) => {
                        if (!v) {
                            this.onDataChange(
                                onChange,
                                {
                                    ...data?.Contribution,
                                    OngoingAdviserChargeInstructions: [],
                                },
                                data?.Contribution,
                                "Contribution"
                            );
                        }
                        this.setState({ includeOAC: v });
                    }}
                    selected={this.state.includeOAC}
                    cssWrapper="radio-group rg-inline"
                />
                {this.state.includeOAC && (
                    <>
                        {oacs.length == 0 ? (
                            <Alert alertType={AlertType.Warning} message={"There is no other subaccounts"} />
                        ) : (
                            <>
                                {oacs.map((oac) => {
                                    return this.renderOACCheckbox(oac);
                                })}
                            </>
                        )}
                    </>
                )}
                <ChargesOnIllustration {...this.getProps("Contribution")} />
            </>
        );
    }
}
